import {
	AdjustEventName,
	affiliationDataAtom,
	EventIdentifier,
	FeatureTeamEvent,
	NotificationService,
	TrackingEventType,
	useDeviceId,
	useIntl,
	useLocale,
	type UserRegistration,
	useServices,
	useSettings
} from '@capsicum/common';
import { BackButton, Button, Notification, TOPBAR_ZINDEX, useRouter } from '@capsicum/common-web';
import {
	countryRegistrationStepAtom,
	isRegisteredSuccessAtom,
	type RegisterProps,
	type RegisterSchemaType,
	showEmailOnAlmostDone,
	useNewRegisterForm,
	useOnboardingAnalytics,
	useRegistrationSubmit
} from '@capsicum/socios-core';
import { useAtomValue, useSetAtom } from 'jotai';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { Shell } from '../../ui';

import { CountryStep } from './steps/CountryStep';
import { EmailAddressStep } from './steps/EmailAddressStep';
import { UsernameStep } from './steps/UsernameStep';

export const Register: FC<RegisterProps> = props => {
	const { type, validationCode } = props;
	const { locale } = useLocale();
	const { replace } = useRouter();

	const {
		settings: { isCountryStepRegistrationStepEnabled, isUsernameStepRegistrationEnabled }
	} = useSettings();

	const {
		countryPrivacyPolicy,
		countryTermsAndConditions,
		handleSuggestionClick,
		isPolicyLoading,
		isUserCountriesQueryLoading,
		ncwTermsAndConditions,
		registerForm: form,
		UScontactUSLink,
		userIPCountry
	} = useNewRegisterForm(locale);

	const deviceUid = useDeviceId() as string;

	const { $t } = useIntl();
	const { searchParams } = useRouter();
	const { analytics, setUserId } = useServices();
	const { sendRegistrationCountryEvent, sendRegistrationEmailEvent } = useOnboardingAnalytics();
	const [step, setStep] = useState(0);
	const submitRegistration = useRegistrationSubmit();
	const [isFormSubmitted, setFormSubmission] = useState(false);
	const setEmailOnAlmostDone = useSetAtom(showEmailOnAlmostDone);
	const setIsRegisteredSuccessAtom = useSetAtom(isRegisteredSuccessAtom);
	const [isCountryStepValid, setCountryStepValidity] = useState(false);
	const [isUsernameStepValid, setUsernameStepValidity] = useState(false);
	const setCountryRegistrationStep = useSetAtom(countryRegistrationStepAtom);
	const [isEmailAddressStepValid, setEmailAddressStepValidity] = useState(false);
	const affiliationData = useAtomValue(affiliationDataAtom);

	const isRegisterByEmail = type === 'email';
	const isEmailStepVisible = !isRegisterByEmail;
	const isCountryStepVisible =
		isCountryStepRegistrationStepEnabled && (isRegisterByEmail || props.countryCode !== userIPCountry?.iso2);
	const isTermsAndConditionsOnEmailStepVisible = !isUsernameStepRegistrationEnabled && !isCountryStepVisible;

	useEffect(() => {
		setCountryRegistrationStep({
			countryPrivacyPolicy,
			countryTermsAndConditions,
			isPolicyLoading,
			isUserCountriesQueryLoading,
			ncwTermsAndConditions
		});
	}, [
		isPolicyLoading,
		countryPrivacyPolicy,
		countryTermsAndConditions,
		setCountryRegistrationStep,
		isUserCountriesQueryLoading,
		ncwTermsAndConditions
	]);

	useEffect(() => {
		if (isRegisterByEmail) form.setValue('email', props.email);
	}, [isRegisterByEmail, form, props]);

	const steps = useMemo(() => {
		const steps: Array<JSX.Element> = [];

		isEmailStepVisible &&
			steps.push(
				<EmailAddressStep
					isTermsAndConditionsVisible={isTermsAndConditionsOnEmailStepVisible}
					key="registration-email-step"
					onValidityChange={setEmailAddressStepValidity}
				/>
			);

		isUsernameStepRegistrationEnabled &&
			steps.push(
				<UsernameStep
					isCountryStepVisible={isCountryStepVisible}
					isEmailStepVisible={isEmailStepVisible}
					key="registration-username-step"
					setUsername={handleSuggestionClick}
					onValidityChange={setUsernameStepValidity}
				/>
			);

		isCountryStepVisible &&
			steps.push(
				<CountryStep
					key="registration-country-step"
					usContactUSLink={UScontactUSLink}
					onValidityChange={setCountryStepValidity}
				/>
			);

		return steps;
	}, [handleSuggestionClick, UScontactUSLink, isEmailStepVisible, isCountryStepVisible]);

	const isLastStep = step === steps.length - 1;

	const isSubmittable = useMemo(() => {
		if (form.formState.isValidating || form.formState.isSubmitting) return false;

		const validations: Array<boolean> = [];

		if (isEmailStepVisible) validations.push(isEmailAddressStepValid);
		if (isUsernameStepRegistrationEnabled) validations.push(isUsernameStepValid);
		if (isCountryStepValid) validations.push(isCountryStepValid);

		return validations[step];
	}, [
		step,
		isCountryStepValid,
		isEmailStepVisible,
		isUsernameStepValid,
		isEmailAddressStepValid,
		isUsernameStepRegistrationEnabled,
		form.formState.isValidating,
		form.formState.isSubmitting
	]);

	const onNext = useCallback(() => {
		analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
			feature_team: FeatureTeamEvent.OFT,
			page: step === 0 ? 'email_entry_page' : 'username_entry_page',
			text: 'Next'
		});
		setStep(Math.min(steps.length - 1, step + 1));
	}, [analytics, step, steps.length]);

	const onBack = useCallback(() => {
		setStep(Math.max(0, step - 1));
	}, [step]);

	const onRegister = useCallback<(props: RegisterSchemaType) => Promise<void>>(
		async ({ country: { iso2, iso3 }, email, isAbove18, marketingConsent, username }: RegisterSchemaType) => {
			try {
				const mobileNumber = isRegisterByEmail ? undefined : props.mobileNumber;

				isEmailStepVisible && sendRegistrationEmailEvent();

				isCountryStepVisible && sendRegistrationCountryEvent();

				analytics.sendEvent(TrackingEventType.track, EventIdentifier.SignUpEnd, {
					feature_team: FeatureTeamEvent.OFT,
					page: step === 1 ? 'username_entry_page' : 'country_entry_page',
					text: 'Register'
				});

				setFormSubmission(true);

				const registration: UserRegistration = {};

				if (affiliationData && Object.keys(affiliationData).length) {
					registration.affiliation = affiliationData;
				}

				const submitResponse = await submitRegistration({
					appType: 'webapp',
					deviceUid,
					email,
					locale,
					mobileNumber,
					registration,
					regulation: iso3 === 'USA' ? 'usa' : 'international',
					userMetadata: { isAbove18, marketingConsent: !!marketingConsent },
					username,
					validationCode,
					vatCountry: iso2
				});

				const submitErrorMsg = submitResponse?.message;

				if (submitErrorMsg) {
					NotificationService.addNotification({
						component: (
							<Notification
								message={submitErrorMsg}
								title={$t('auth.registrationError')}
								variant="error"
							/>
						)
					});
				} else {
					setUserId(submitResponse?.userId || '');
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.SignUpEnd, {
						feature_team: FeatureTeamEvent.OFT,
						page: 'registration_page',
						text: 'Register'
					});
					analytics.sendEvent(TrackingEventType.identify, submitResponse?.userId || '', {
						email,
						mobile_number: mobileNumber,
						username
					});
					analytics.sendEvent(TrackingEventType.track, AdjustEventName.USER_REGISTRATION);
					replace(`/?${searchParams.toString()}`);
					setIsRegisteredSuccessAtom(true);

					if (!isRegisterByEmail) {
						setEmailOnAlmostDone(email);
					}
				}
			} catch {
				NotificationService.addNotification({
					component: (
						<Notification
							message={$t('auth.registrationError')}
							title={$t('auth.registrationError')}
							variant="error"
						/>
					)
				});
			} finally {
				setFormSubmission(false);
			}
		},
		[
			$t,
			step,
			replace,
			props,
			locale,
			analytics,
			searchParams,
			validationCode,
			isCountryStepVisible,
			isEmailStepVisible,
			isRegisterByEmail,
			sendRegistrationCountryEvent,
			sendRegistrationEmailEvent,
			submitRegistration,
			setEmailOnAlmostDone,
			setIsRegisteredSuccessAtom,
			deviceUid
		]
	);

	return (
		<S.Wrapper>
			<S.Header>{step > 0 && <BackButton testID="registration-form-back-button" onClick={onBack} />}</S.Header>

			<FormProvider {...form}>
				<S.Form>
					<Shell
						buttons={[
							<Button
								disabled={!isSubmittable}
								fullWidth
								isLoading={isFormSubmitted}
								key="confirm"
								testID="registration-form-button"
								text={$t(isLastStep ? 'onboarding.buttons.register' : 'global.button.next')}
								type="button"
								onClick={isLastStep ? form.handleSubmit(onRegister) : onNext}
							/>
						]}
						showLogo={false}
					>
						{steps[step]}
					</Shell>
				</S.Form>
			</FormProvider>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		width: 100vw;
		height: 100vh;
		flex-direction: column;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const Form = styled.form`
		flex: 1;
	`;

	export const Header = styled.div`
		top: 0;
		display: flex;
		position: sticky;
		align-items: center;
		z-index: ${TOPBAR_ZINDEX};
		padding: ${({ theme }) => theme.spacings.small};
		flex: 0 0 ${({ theme }) => theme.sizing.xxlarge};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};

		border-style: ${({ theme }) => theme.borderStyles.divider.style};
		border-width: ${({ theme }) => theme.borderStyles.divider.width};
		border-color: ${({ theme }) => theme.colors.borders.main.divider};
	`;
}
